import { ReactElement, useEffect, useState } from 'react';
import {
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { FormattedMessage, formatTranslation } from 'intl';
import { useIntl } from 'react-intl';
import { CommonTranslationKeys, DocumentationTranslationKeys } from 'intl/keys';
import { ButtonContained, ButtonText } from 'components/general';
import { formatVersionNumber } from 'shared/domain/documentation/versionState';
import { dialogContentSx } from './styles';

/** What the user chose about the new version. */
export interface AddVersionChoice {
  name: string;
  makeCurrent: boolean;
}

export interface AddVersionDialogProps {
  isOpen: boolean;
  documentationName: string;
  fileName: string;
  versionNumber: number;
  defaultName: string;
  defaultMakeCurrent: boolean;
  /** While the version is being added the dialog cannot be closed and the choice cannot be confirmed again. */
  loading?: boolean;
  onConfirm: (choice: AddVersionChoice) => void;
  onCancel: () => void;
}

/** Adds a file as a version of a documentation (PT-7316 extends it with uploading a file of its own). */
export function AddVersionDialog({
  isOpen,
  documentationName,
  fileName,
  versionNumber,
  defaultName,
  defaultMakeCurrent,
  loading = false,
  onConfirm,
  onCancel,
}: AddVersionDialogProps): ReactElement {
  const intl = useIntl();
  const [name, setName] = useState(defaultName);
  const [makeCurrent, setMakeCurrent] = useState(defaultMakeCurrent);

  useEffect(() => {
    if (isOpen) {
      setName(defaultName);
      setMakeCurrent(defaultMakeCurrent);
    }
  }, [isOpen, defaultName, defaultMakeCurrent]);

  const cancel = loading ? undefined : onCancel;

  return (
    <Dialog open={isOpen} onClose={cancel} fullWidth maxWidth='xs' data-qa='add-version-dialog'>
      <DialogTitle>
        <FormattedMessage id={DocumentationTranslationKeys.addVersionTitle} />
        <Typography variant='body2' color='text.secondary'>
          {documentationName}
        </Typography>
      </DialogTitle>
      <DialogContent sx={dialogContentSx}>
        <Typography variant='body2'>{fileName}</Typography>
        <TextField
          size='small'
          disabled
          label={formatTranslation(intl, DocumentationTranslationKeys.addVersionNumberLabel)}
          value={formatVersionNumber(versionNumber) ?? ''}
        />
        <TextField
          size='small'
          autoFocus
          label={formatTranslation(intl, DocumentationTranslationKeys.addVersionNameLabel)}
          value={name}
          onChange={(event) => setName(event.target.value)}
          inputProps={{ 'data-qa': 'add-version-name' }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={makeCurrent}
              onChange={(_, checked) => setMakeCurrent(checked)}
            />
          }
          label={formatTranslation(intl, DocumentationTranslationKeys.addVersionMarkCurrent)}
        />
      </DialogContent>
      <DialogActions>
        <ButtonText onClick={cancel} disabled={loading}>
          <FormattedMessage id={CommonTranslationKeys.cancel} />
        </ButtonText>
        <ButtonContained
          onClick={() => onConfirm({ name, makeCurrent })}
          disabled={loading}
          endIcon={loading ? <CircularProgress size={16} color='inherit' /> : undefined}
        >
          <FormattedMessage id={CommonTranslationKeys.add} />
        </ButtonContained>
      </DialogActions>
    </Dialog>
  );
}
